import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { BasisBadge } from "@/components/trust/BasisBadge";
import { ConfidenceIndicator } from "@/components/trust/ConfidenceIndicator";

function TrendChart({ points }) {
  if (!points || points.length < 2) {
    return <div className="text-xs text-[var(--oim-text-dim)] py-10 text-center">Not enough history yet.</div>;
  }
  const values = points.map((p) => p.value);
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const W = 800, H = 180, PAD = 8;
  const coords = values.map((v, i) => [
    PAD + (i / (values.length - 1)) * (W - PAD * 2),
    H - PAD - ((v - min) / range) * (H - PAD * 2),
  ]);
  const path = coords.map((c) => c.join(",")).join(" ");
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44" data-testid="tag-detail-chart" preserveAspectRatio="none">
        <polyline points={path} fill="none" stroke="var(--oim-green)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div className="flex justify-between text-[9px] font-mono-data text-[var(--oim-text-dim)] mt-1">
        <span>min {min}</span>
        <span>max {max}</span>
      </div>
    </div>
  );
}

export default function TagDetail() {
  const { tagId } = useParams();
  const { data: current, error } = usePolling(
    () => api.get("/tags/current").then((r) => r.data.find((t) => t.tag_id === tagId)),
    8000, [tagId]
  );
  const { data: history } = usePolling(
    () => api.get(`/tags/${tagId}/history`, { params: { limit: 100 } }).then((r) => r.data),
    8000, [tagId]
  );

  if (error || (current === undefined && !history)) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="tag-detail-error">Could not load tag.</div>;
  if (!current) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="tag-detail-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="tag-detail-page">
      <Link to="/tags" className="inline-flex items-center gap-1.5 text-xs text-[var(--oim-text-muted)] hover:text-[var(--oim-text)]" data-testid="tag-detail-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Tag List
      </Link>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid="tag-detail-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-data text-lg font-bold text-[var(--oim-text)]">{current.tag_id}</div>
            <div className="text-xs text-[var(--oim-text-secondary)] mt-1">{current.name} — {current.element_name}</div>
            <div className="text-[9px] uppercase tracking-wide text-[var(--oim-text-dim)] mt-1">{current.chain} · provenance: {current.provenance}</div>
          </div>
          <div className="text-right">
            <div className="font-mono-data text-2xl font-bold text-[var(--oim-text)]">{current.value} <span className="text-sm text-[var(--oim-text-dim)]">{current.unit}</span></div>
            <div className="text-[10px] text-[var(--oim-text-dim)] mt-1">Last updated {new Date(current.last_updated).toLocaleTimeString()}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3">
          <BasisBadge basis={current.basis} />
          <ConfidenceIndicator level={current.confidence} />
        </div>
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Recent history</div>
        <TrendChart points={history} />
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">History table (latest 20)</div>
        <div className="space-y-1" data-testid="tag-detail-history-table">
          {history && [...history].reverse().slice(0, 20).map((h, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 py-1 text-xs border-b" style={{ borderColor: "var(--oim-border)" }} data-testid={`tag-history-row-${i}`}>
              <span className="font-mono-data text-[var(--oim-text-dim)]">{new Date(h.valid_time).toLocaleTimeString()}</span>
              <span className="font-mono-data text-[var(--oim-text)] font-semibold">{h.value}</span>
              <span className="text-[var(--oim-text-secondary)]">{h.basis}</span>
              <span className="text-[var(--oim-text-secondary)]">{h.confidence}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
